import { useCallback, useMemo } from 'react';
import { FileUser } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useSearchParams } from 'react-router-dom';
import { ContractTypes } from '@duke-hq/shared-constants/enrolments';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export function useContractFilter(): string[] {
  const [searchParams] = useSearchParams();
  return useMemo(() => {
    const raw = searchParams.get('contractTypes') ?? '';
    const validValues = new Set(ContractTypes.map((contract) => contract.value));
    return raw.split(',').filter((value) => validValues.has(value));
  }, [searchParams]);
}

export function ContractFilter(): React.JSX.Element {
  const [, setSearchParams] = useSearchParams();
  const contractTypes = useContractFilter();

  const options = useMemo(
    () =>
      ContractTypes.map((contract) => ({
        id: contract.value,
        value: contract.value,
        label: contract.label,
      })),
    [],
  );

  const handleChange = useCallback(
    (filters: MultipleChoiceFilterOption) => {
      const nextContractTypes = filters.contractTypes ?? [];
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextContractTypes.length === 0) {
            next.delete('contractTypes');
          } else {
            next.set('contractTypes', nextContractTypes.join(','));
          }
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <MultipleChoiceFilter
      items={options}
      label={t`Contract`}
      name="contractTypes"
      onFilterChange={handleChange}
      onlyUpdateOnChange
      value={contractTypes.join(',')}
      variant="ghost"
      leftIcon={<FileUser size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}
